// The main thread's side of the geometry worker: start, wait for ready, call, recycle.
// manifold-3d 3.5.3 leaks ~4KB of wasm heap per evaluated extrude even when every handle is
// deleted (measured 2026-09-21: ~80KB per tag, linear). A wasm heap never shrinks, so the
// worker is replaced after `recycleAfter` meshes/exports, while idle. Layout requests are 2D
// only and do not count.
export function createWorkerClient({ url, recycleAfter = 300, onFatal }) {
  let worker = null, ready = null, generations = 0, reqId = 0;
  const pending = new Map();
  function start() {
    const w = new Worker(url, { type: 'module' });
    generations = 0;
    ready = new Promise(res => { w.onceReady = res; });
    w.onmessage = (ev) => {
      const { type, id } = ev.data;
      if (type === 'ready') { w.onceReady(); return; }
      if (type === 'fatal') { onFatal(ev.data.message); return; }
      if (type === 'progress') { for (const pid of ev.data.ids) pending.get(pid)?.onProgress?.(ev.data); return; }   // one download, many waiters
      const cb = pending.get(id);
      if (!cb) return;
      pending.delete(id);
      cb.resolve(ev.data);
    };
    worker = w;
  }
  // Always waits for the CURRENT worker to be ready: the worker ignores messages that arrive
  // before its 'ready', so posting early would leave this promise pending forever.
  async function call(msg, { onProgress } = {}) {
    let w;
    do { w = worker; await ready; } while (w !== worker);          // the worker may be recycled while we wait
    return new Promise(resolve => {
      const id = ++reqId;                                          // the sequence number the reply echoes
      pending.set(id, { resolve, onProgress });
      worker.postMessage({ ...msg, id });
    });
  }
  function recycleIfDue() {
    if (generations < recycleAfter || pending.size > 0) return;
    worker.terminate();
    start();
  }
  start();
  return { call, recycleIfDue, whenReady: () => ready, countGeneration: () => { generations++; } };
}
